import React, { useState } from 'react';
import { Box, Menu, styled, Stack, Badge, Tooltip } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_VisibilityState,
} from 'material-react-table';
import { GridFilterModel, GridLogicOperator, GridFilterItem, GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from 'react-query';
import { bulkDeleteReports, getAllReports } from '../components/report/report.api';
import { handleError } from '../common/utils/util.errors';
import dataKeys from '../common/react-query/data-keys';
import { ROUTES } from '../common/constants/urls';
import { NAVBAR_HEIGHT } from '../common/constants/general';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Delete as DeleteIcon, Launch, ViewColumnOutlined as ViewColumnIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { getDateOperators, getNumericOperators, getQueryStringForSieve, getStringOperators } from '../common/utils/datagrid-sieve.util';
import {
  formatCurrency,
  formatISODate,
  formatPercentage,
  getFullName,
  joinDateAndUser,
  camelCaseToSpaceSeparated,
} from '../common/utils/common.util';
import { toast } from 'react-toastify';
import { deleteReport } from '../components/deleteItem/deleteItem.api';
import DeletionHandler from '../components/deleteItem/deleteItem';
import { ProjectInfoDTO } from '../components/project/project.type';
import { Typography } from 'components/index';
import TableSkeletonLoader from '../components/general/TableSkeletonLoader';
import { useAuth } from 'contexts/AuthContext';
import { getFromLocalStorage, storeInLocalStorage } from 'common/utils/util.cache';
import { useDebounce } from '@uidotdev/usehooks';
import { getAppliedFiltersCount } from '../components/filter-panel/filter-panel.util';
import FilterPanel from '../components/filter-panel/FilterPanel';
import { usePrompt } from '../components/AppPrompt';
import WhiteFab from '../components/common/WhiteFab';
import { CustomTooltip } from '../components/common/CustomTooltip';
import { IconOverTextButton } from '../components/common/IconOverTextButton';
import { useMRTStyling } from '../hooks/useMRTStyling';
import { CustomManageColumnsMenu } from '../components/common/CustomManageColumnsMenu';
import { useHasActionRole } from '../components/common/PermissionGate';

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_MS = 500;
const cacheKey = `reports-table-preferences`;
const FILTERS_KEY = 'reports-table-filters';
const DEFAULT_FILTERS: GridFilterModel = { items: [], logicOperator: GridLogicOperator.And };

const getInitialFilters = (items: GridFilterItem[]) => {
  const savedFilters = getFromLocalStorage(FILTERS_KEY);
  return items
    ? {
        items: items,
        logicOperator: GridLogicOperator.And,
      }
    : DEFAULT_FILTERS;
};

const saveFilters = (filters: GridFilterModel) => {
  storeInLocalStorage(FILTERS_KEY, filters);
};

const StyledDeleteIcon = styled(DeleteIcon)<{ color?: string }>(({ theme, color = 'gray' }) => ({
  color: color,
  '&:hover': {
    color: theme.palette.error.main,
  },
}));

type Report = {
  id: string;
  reportName: string;
  project: ProjectInfoDTO;
  status: string;
  createdOn: string;
  createdBy: any;
  deviationPercentage: number;
  contractorTotal: number;
  cdccTotal: number;
  comparablesCount: number;
};

const ReportsList = () => {
  const { commonMRTStyling, getRowStyling, getActionsColumnStyling, getOnSortingChangeWithToast } = useMRTStyling<Report>();
  const [paginationModel, setPaginationModel] = React.useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortingModel, setSortingModel] = React.useState([{ id: 'createdOn', desc: true }]);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(DEFAULT_FILTERS);
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = React.useState(
    getQueryStringForSieve({ page: 0, pageSize: DEFAULT_PAGE_SIZE }, [{ field: 'createdOn', sort: 'desc' }], {
      items: [],
      logicOperator: GridLogicOperator.And,
    }),
  );
  const debouncedQueryParams = useDebounce(queryParams, DEBOUNCE_MS);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const queryClient = useQueryClient();
  const [initialDataLoaded, setInitialDataLoaded] = useState(true);
  const [selectedReport, setSelectedReport] = React.useState<any | null>(null);
  const { getConfirmation } = usePrompt();
  const [selectedReports, setSelectedReports] = React.useState<string[]>([]);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = React.useState(false);
  const [loadingRows, setLoadingRows] = React.useState<Set<string>>(new Set());
  const rowCountRef = React.useRef(0);
  const [columnVisibility, setColumnVisibility] = React.useState<MRT_VisibilityState>({
    createdBy: false,
    createdOn: false,
    contractorTotal: false,
    cdccTotal: false,
    comparablesCount: false,
  });

  const openDeleteConfirmation = (row: any) => {
    setSelectedReport(row);
  };

  React.useEffect(() => {
    if (selectedReport) {
      setShowDeleteDialog(true);
    } else {
      setShowDeleteDialog(false);
    }
  }, [selectedReport]);

  React.useEffect(() => {
    if (searchParams.get('showOnlyMyReports') == 'true') {
      toggleMyReportsFilter();
    }
  }, [searchParams]);

  const handleBulkDelete = async () => {
    const reportIds = selectedReports;
    if (reportIds.length === 0) {
      toast.error('Please select at least one report!');
      return;
    }
    setBulkDeleteInProgress(true);
    const isConfirmed = await getConfirmation({
      heading: 'Delete Reports',
      description: 'Are you sure you want to delete these report(s)?',
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });

    if (isConfirmed) {
      try {
        await bulkDeleteReports(reportIds);
        toast.success('Reports(s) deleted successfully');
        queryClient.invalidateQueries([dataKeys.allReports, debouncedQueryParams]);
        setSelectedReports([]);
      } catch (error) {
        console.error('Error deleting projects:', error);
      }
    }
    setBulkDeleteInProgress(false);
  };
  const canDeleteReports = useHasActionRole('DELETE_CA_REPORT');

  const columns = React.useMemo<MRT_ColumnDef<Report>[]>(() => {
    const baseColumns: MRT_ColumnDef<Report>[] = [
      {
        accessorKey: 'reportName',
        header: 'Report Name',
        size: 200,
        Cell: ({ row }: { row: MRT_Row<Report> }) => (
          <Box p={1} width={'100%'}>
            <CustomTooltip title={row.original.reportName}>
              <Stack>
                <Typography variant='subtitle2' fontWeight={'bold'} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.original.reportName}
                </Typography>
                <Typography variant='caption'>
                  {joinDateAndUser(row.original.createdOn, row.original.createdBy, 'Generated On ')}
                </Typography>
              </Stack>
            </CustomTooltip>
          </Box>
        ),
      },
      {
        accessorKey: 'project',
        header: 'Project',
        size: 120,
        Cell: ({ row }: { row: MRT_Row<Report> }) => {
          const project = row.original.project as ProjectInfoDTO;
          if (!project) return '--';

          return (
            <Box p={1} width='100%'>
              <Stack>
                <CustomTooltip title={project.projectTitle}>
                  <Link
                    to={`${ROUTES.PROJECTS}/${project.projectId}`}
                    target='_blank'
                    onClick={e => e.stopPropagation()}
                    style={{
                      textDecoration: 'none',
                      color: 'unset',
                      display: 'inline-flex',
                      alignItems: 'center',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                        minWidth: 0,
                      }}
                    >
                      {project.projectTitle || '--'}
                    </Box>
                    <Launch
                      sx={{
                        fontSize: '16px',
                        ml: 0.5,
                        flexShrink: 0,
                      }}
                      color='primary'
                    />
                  </Link>
                </CustomTooltip>
                <Typography variant='caption'>{`${row.original.comparablesCount || 'No'} comparables added`}</Typography>
              </Stack>
            </Box>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        minSize: 150,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => camelCaseToSpaceSeparated(row.original.status || '--'),
      },
      {
        accessorKey: 'createdOn',
        header: 'Generated On',
        minSize: 150,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => {
          if (!row.original.createdOn) return '--';
          return formatISODate(row.original.createdOn);
        },
      },
      {
        accessorKey: 'createdBy',
        header: 'Generated By',
        minSize: 150,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => {
          const createdBy = row.original.createdBy;
          return createdBy ? getFullName(createdBy) : '--';
        },
      },

      {
        accessorKey: 'deviationPercentage',
        header: 'Deviation',
        minSize: 200,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => (
          <Box p={1} width={'100%'}>
            <Stack>
              <Typography variant='subtitle2' fontWeight='bold' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.original.deviationPercentage ? formatPercentage((row.original.deviationPercentage * 100).toString()) : '--'}
              </Typography>
              <Typography variant='caption'>Contractor Total: {formatCurrency((row.original.contractorTotal || 0).toString())}</Typography>
              <Typography variant='caption'>CDCC Total: {formatCurrency((row.original.cdccTotal || 0).toString())}</Typography>
            </Stack>
          </Box>
        ),
      },
      {
        accessorKey: 'contractorTotal',
        header: 'Contractor Total',
        minSize: 150,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => formatCurrency((row.original.contractorTotal || 0).toString()),
      },
      {
        accessorKey: 'cdccTotal',
        header: 'CDCC Total',
        minSize: 150,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => formatCurrency((row.original.cdccTotal || 0).toString()),
      },
      {
        accessorKey: 'comparablesCount',
        header: 'Comparables',
        minSize: 120,
        grow: 1,
        Cell: ({ row }: { row: MRT_Row<Report> }) => row.original.comparablesCount || 0,
      },
    ];

    const actionColumn: MRT_ColumnDef<Report> = {
      id: 'actions',
      header: '',
      size: 60,
      minSize: 60,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableHiding: false,
      ...getActionsColumnStyling(),
      Header: ({ table }: { table: any }) => (
        <Stack sx={{ width: '100%' }} direction='row' spacing={1} alignItems='center' justifyContent='center'>
          <Tooltip title='Delete Selected Reports' arrow={false}>
            <span>
              <WhiteFab
                size='small'
                icon={<DeleteIcon fontSize='small' />}
                textColor='error'
                onClick={handleBulkDelete}
                disabled={selectedReports.length === 0}
                loading={bulkDeleteInProgress}
              />
            </span>
          </Tooltip>
        </Stack>
      ),
      Cell: ({ row }: { row: MRT_Row<Report> }) => (
        <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center' sx={{ width: '100%' }}>
          <Tooltip title='Delete Report' arrow={false}>
            <span>
              <WhiteFab
                size='small'
                icon={<DeleteIcon fontSize='small' />}
                textColor='error'
                disabled={selectedReports.length > 0}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  openDeleteConfirmation(row.original);
                }}
              />
            </span>
          </Tooltip>
        </Stack>
      ),
    };

    return canDeleteReports ? [...baseColumns, actionColumn] : baseColumns;
  }, [selectedReports.length, bulkDeleteInProgress, canDeleteReports]);

  // Create compatible GridColDef for FilterPanel
  const filterColumns: GridColDef[] = [
    {
      field: 'reportName',
      headerName: 'Report Name',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'createdOn',
      headerName: 'Generated On',
      type: 'date',
      filterOperators: getDateOperators(),
    },
    {
      field: 'createdBy',
      headerName: 'Generated By',
      type: 'string',
      filterOperators: getStringOperators(),
    },

    {
      field: 'deviationPercentage',
      headerName: 'Deviation',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'contractorTotal',
      headerName: 'Contractor Total',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'cdccTotal',
      headerName: 'CDCC Total',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'comparablesCount',
      headerName: 'Comparables',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
  ];

  const skippedFields: string[] = [];
  const filteredColumns = filterColumns.filter(column => !skippedFields.includes(column.field));

  const toggleMyReportsFilter = () => {
    const myProjectsFilter = {
      field: 'createdBy.Id',
      operator: 'equals',
      value: user?.id ?? '',
    };
    const filterItems = filterModel.items.filter((item: GridFilterItem) => item.field !== myProjectsFilter.field);
    if (myReportsFilterOn) {
      setFilterModel({ ...filterModel, items: filterItems });
    } else {
      setFilterModel({ ...filterModel, items: [...filterItems, myProjectsFilter] });
    }
  };

  const myReportsFilterOn = React.useMemo(() => {
    return filterModel.items.some((item: GridFilterItem) => item.field === 'createdBy.Id');
  }, [filterModel.items]);

  const showManageColumns = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setColumnsMenuAnchorEl(e.currentTarget);
  };

  const { data, isLoading, isError, error } = useQuery(
    [dataKeys.allReports, debouncedQueryParams],
    () => getAllReports(debouncedQueryParams),
    {
      onSettled: () => {
        setInitialDataLoaded(false);
      },
    },
  );

  React.useEffect(() => {
    setQueryParams(
      getQueryStringForSieve(
        { page: paginationModel.pageIndex, pageSize: paginationModel.pageSize },
        sortingModel.map(sort => ({ field: sort.id, sort: sort.desc ? 'desc' : 'asc' })),
        filterModel,
      ),
    );
  }, [paginationModel, sortingModel, filterModel]);

  if (isError) handleError(error);
  const reports: any[] = data?.reportsList ?? [];
  const total = data?.total ?? 0;

  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});

  React.useEffect(() => {
    const selectedIds = Object.keys(rowSelection);
    setSelectedReports(selectedIds);
  }, [rowSelection]);

  const getPreferences = () => {
    return getFromLocalStorage(cacheKey);
  };

  const savePreferences = () => {
    const state = table.getState();
    storeInLocalStorage(cacheKey, state);
  };

  const resetTable = () => {
    setFilterModel(DEFAULT_FILTERS);
    setPaginationModel({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
    setSortingModel([{ id: 'createdOn', desc: true }]);
    setColumnVisibility({
      'createdBy.Id': false,
      createdBy: false,
      createdOn: false,
      contractorTotal: false,
      cdccTotal: false,
      comparablesCount: false,
    });
  };

  const actions = [{ label: 'Reset Table', onClick: resetTable }];

  // Handle row click to navigate to report details
  const handleRowClick = (row: MRT_Row<Report>) => {
    const reportId = row.original.id;
    navigate(`${ROUTES.CA_REPORTS}/${reportId}`, { state: { fromReportsList: true, reportId: reportId } });
  };

  const rowCount = React.useMemo(() => {
    if (data && data.total !== undefined) {
      rowCountRef.current = data.total;
    }
    return rowCountRef.current;
  }, [data?.total]);

  const table = useMaterialReactTable<Report>({
    ...commonMRTStyling,
    columns,
    data: reports,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableMultiRowSelection: canDeleteReports,
    enableRowSelection: canDeleteReports,
    getRowId: (row: Report) => row.id,
    onPaginationChange: setPaginationModel,
    onSortingChange: getOnSortingChangeWithToast(sortingModel as any, setSortingModel as any),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      pagination: paginationModel,
      sorting: sortingModel,
      columnVisibility,
      density: 'compact',
      isLoading: isLoading,
      rowSelection,
    },
    rowCount: rowCount,
    pageCount: Math.ceil(rowCount / paginationModel.pageSize),
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<Report> }) => {
      // Determine row background color
      let backgroundColor = '';

      // Use zebra striping for alternating rows
      backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';

      return {
        onClick: () => handleRowClick(row),
        sx: {
          cursor: 'pointer',
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: theme => theme.palette.action.hover,
          },
          // Style pinned columns to match row background
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: backgroundColor,
            zIndex: -1,
          },
          '& td[data-pinned="true"]': {
            zIndex: 5,
          },
        },
      };
    },
    muiTableBodyCellProps: ({ row }: { row: MRT_Row<Report> }) => ({
      sx: {
        border: 'none',
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
      },
    }),

    muiBottomToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        borderRadius: 'inherit',
        border: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-select'],
        right: ['actions'],
      },
    },
  });

  if (isLoading && initialDataLoaded) return <TableSkeletonLoader />;

  return (
    <Box mt={2}>
      <Box sx={{ height: `calc(100vh - ${NAVBAR_HEIGHT} - 44px)` }} my={2}>
        <Stack direction={'row'} alignItems='center' justifyContent={'flex-end'} p={1} spacing={2}>
          <Stack direction={'row'} alignItems={'center'} spacing={0}>
            <IconOverTextButton
              icon={<ViewColumnIcon />}
              text='Columns'
              tooltip='Manage Columns'
              onClick={showManageColumns}
              showHoverEffect={false}
              wrapInSpan={true}
            />

            <Badge badgeContent={getAppliedFiltersCount(filterModel, skippedFields)} color='primary' overlap='circular'>
              <IconOverTextButton
                icon={<FilterListIcon />}
                text='Filter'
                tooltip='Filter'
                onClick={(e: any) => setFilterAnchorEl(e.currentTarget)}
                showHoverEffect={false}
              />
            </Badge>
          </Stack>
        </Stack>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => {
            setFilterAnchorEl(null);
          }}
        >
          <FilterPanel columns={filteredColumns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} />
        </Menu>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme => theme.palette.background.paper,
            overflow: 'hidden',
          }}
        >
          <MaterialReactTable table={table} />
        </Box>
      </Box>
      {/* DeleteConfirmationComponent*/}
      {selectedReport && (
        <DeletionHandler
          open={showDeleteDialog}
          item={selectedReport}
          deleteFunction={deleteReport}
          onDeleteConfirm={(id, message) => {
            queryClient.invalidateQueries(dataKeys.allReports);
            toast.success(message);
            setShowDeleteDialog(false);
            setSelectedReport(null);
          }}
          onClose={() => {
            setShowDeleteDialog(false);
            setSelectedReport(null);
          }}
        />
      )}
      {/* Custom Manage Columns Menu */}
      <CustomManageColumnsMenu anchorEl={columnsMenuAnchorEl} setAnchorEl={setColumnsMenuAnchorEl} table={table} title='Manage Columns' />
    </Box>
  );
};

export default ReportsList;
